import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield } from "lucide-react";
import AppFooter from "@/components/layout/AppFooter";

/**
 * Privacy Policy page. Publicly accessible.
 */
const Section = ({ n, title, children }) => (
  <section className="space-y-3" data-testid={`privacy-section-${n}`}>
    <h2 className="text-lg font-semibold tracking-tight">{n}. {title}</h2>
    <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">{children}</div>
  </section>
);

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="privacy-page">
      <header className="border-b">
        <div className="mx-auto max-w-3xl px-5 py-4 flex items-center justify-between">
          <Link to="/login" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <div className="inline-flex items-center gap-2 text-sm font-semibold">
            <Shield className="h-4 w-4 text-primary" /> CSE Investor
          </div>
          <div className="w-16" />
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-3xl px-5 py-10">
        <div className="space-y-1.5 mb-8">
          <h1 className="text-3xl font-bold tracking-tight">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground">Version 1.0 &middot; Last updated 7 May 2026</p>
        </div>

        <Card>
          <CardContent className="p-6 md:p-8 space-y-8">
            <Section n={1} title="What We Collect">
              <p>When you create an account and use this platform we store:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Account details (email, name, phone number if provided)</li>
                <li>Authentication data (securely hashed password, login timestamps, IP address of last login)</li>
                <li>Platform usage data (portfolio holdings you add, watchlists, alert preferences, screener filters)</li>
                <li>Acceptance records (Terms &amp; Disclaimer version and timestamp)</li>
                <li>Anonymous product telemetry (pages visited, buttons clicked) used only to improve the product</li>
              </ul>
            </Section>

            <Section n={2} title="How We Use It">
              <p>We use the data above to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Provide and secure your account</li>
                <li>Personalise your portfolio, watchlist, alerts and screener experience</li>
                <li>Send you transactional emails (password resets, signal alerts, weekly summaries) if you opt in</li>
                <li>Improve the platform and fix bugs</li>
                <li>Comply with legal obligations and prevent abuse</li>
              </ul>
              <p>
                We do <strong>not</strong> sell your personal data. We do not use your
                portfolio holdings for targeted advertising.
              </p>
            </Section>

            <Section n={3} title="Third Parties We Share Data With">
              <p>We share a minimal amount of data with carefully-selected service providers:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Transactional email providers (to deliver your alerts and account emails)</li>
                <li>Cloud infrastructure providers (to host the platform)</li>
                <li>AI model providers (for analysis features &mdash; anonymised prompts only, never your portfolio)</li>
                <li>Payment processors (only if you upgrade &mdash; we never see your full card number)</li>
              </ul>
            </Section>

            <Section n={4} title="Data Retention">
              <p>
                We keep your account data for as long as your account is active. You can
                delete your account at any time from Settings; we purge personal data within
                30 days of deletion, except for records we are legally required to keep.
              </p>
            </Section>

            <Section n={5} title="Cookies">
              <p>
                We use essential cookies (to keep you signed in) and anonymous analytics
                cookies. We do not use advertising or tracking cookies.
              </p>
            </Section>

            <Section n={6} title="Your Rights">
              <p>You have the right to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Access a copy of your personal data</li>
                <li>Correct inaccurate data</li>
                <li>Delete your account and associated personal data</li>
                <li>Export your data in a machine-readable format</li>
                <li>Withdraw marketing consent at any time</li>
              </ul>
              <p>Contact us at <span className="font-mono">privacy@promortek.com</span> to exercise any of these rights.</p>
            </Section>

            <Section n={7} title="Security">
              <p>
                Passwords are hashed using industry-standard algorithms. Data is encrypted
                in transit (HTTPS) and at rest. Access to production systems is restricted
                and audited.
              </p>
            </Section>

            <Section n={8} title="Changes to This Policy">
              <p>
                We may update this Privacy Policy. Material changes will be announced on
                this page and, where appropriate, via email.
              </p>
            </Section>
          </CardContent>
        </Card>

        <div className="mt-8 flex justify-center">
          <Link to="/login">
            <Button variant="outline" data-testid="privacy-back-to-login">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Sign in
            </Button>
          </Link>
        </div>
      </main>

      <AppFooter />
    </div>
  );
};

export default PrivacyPage;
